import { useState, useEffect } from "react";
import {
  Calculator, TrendingUp, Calendar, IndianRupee, PieChart, Phone,
  RefreshCw, Info, CheckCircle, ChevronDown, Building, Car, Coins,
  GraduationCap, Briefcase, Home
} from "lucide-react";

export default function EmiCalculator({ setLoanData }) {
  const [amount, setAmount] = useState(5000000);
  const [rate, setRate] = useState(8.5);
  const [tenure, setTenure] = useState(20);
  const [emi, setEmi] = useState(null);
  const [totalPayment, setTotalPayment] = useState(null);
  const [totalInterest, setTotalInterest] = useState(null);
  const [animateResult, setAnimateResult] = useState(false);
  const [loanType, setLoanType] = useState("home");
  const [selectedLoanName, setSelectedLoanName] = useState("Home Loan");
  const [showLoanMenu, setShowLoanMenu] = useState(false);

  const businessLoanSubItems = {
    'Overview': [
      { name: 'Business Loan', amount: 2000000, rate: 10, tenure: 7 },
      { name: 'Business Loan Interest Rates', amount: 2000000, rate: 10.5, tenure: 7 },
      { name: 'Business Loan low CIBIL Score', amount: 1500000, rate: 12, tenure: 5 }
    ],
    'By Schemes': [
      { name: 'Dairy Farming Loan', amount: 1000000, rate: 9.5, tenure: 7 },
      { name: 'Small Business Loan', amount: 500000, rate: 11, tenure: 5 },
      { name: 'Goat Farming Loan', amount: 800000, rate: 9.5, tenure: 7 },
      { name: 'Startups Loan', amount: 3000000, rate: 11.5, tenure: 7 },
      { name: 'Poultry Farm Loan', amount: 1200000, rate: 9.5, tenure: 7 },
      { name: 'Professional Loan', amount: 2500000, rate: 10.5, tenure: 7 }
    ],
    'By Need': [
      { name: 'Mudra Loan', amount: 1000000, rate: 8.5, tenure: 5 },
      { name: 'PMEGP Loan', amount: 2500000, rate: 8, tenure: 7 },
      { name: 'Letter of Credit', amount: 5000000, rate: 9, tenure: 3 },
      { name: 'CGTMSE Loan', amount: 2000000, rate: 9.5, tenure: 7 },
      { name: 'Overdraft Loan', amount: 1000000, rate: 11, tenure: 3 },
      { name: 'Working Capital Loan', amount: 3000000, rate: 10, tenure: 5 }
    ],
    'By Profession': [
      { name: 'Loan for CA', amount: 2000000, rate: 10, tenure: 7 },
      { name: 'Loan for Doctors', amount: 3000000, rate: 9.5, tenure: 10 }
    ]
  };

  const homeLoanSubItems = {
    'Overview': [
      { name: 'Home Loan', amount: 5000000, rate: 8.5, tenure: 20 },
      { name: 'Home Loan Interest Rates', amount: 5000000, rate: 8.75, tenure: 20 },
      { name: 'Home Loan Balance Transfer', amount: 4000000, rate: 8.3, tenure: 15 },
      { name: 'Home Loan Low CIBIL Score', amount: 3000000, rate: 10, tenure: 15 }
    ],
    'By Amount': [
      { name: '10 Lakh Home Loan', amount: 1000000, rate: 8.5, tenure: 15 },
      { name: '15 Lakh Home Loan', amount: 1500000, rate: 8.5, tenure: 15 },
      { name: '20 Lakh Home Loan', amount: 2000000, rate: 8.5, tenure: 20 },
      { name: '30 Lakh Home Loan', amount: 3000000, rate: 8.5, tenure: 20 },
      { name: '40 Lakh Home Loan', amount: 4000000, rate: 8.5, tenure: 20 },
      { name: '60 Lakh Home Loan', amount: 6000000, rate: 8.5, tenure: 25 }
    ],
    'By Schemes': [
      { name: 'Home Renovation Loan', amount: 1500000, rate: 9, tenure: 10 },
      { name: 'Plot Loan', amount: 2000000, rate: 9.5, tenure: 15 },
      { name: 'Top up Home Loan', amount: 1000000, rate: 9.5, tenure: 10 },
      { name: 'Home Construction Loan', amount: 3000000, rate: 8.75, tenure: 15 },
      { name: 'NRI Home Loan', amount: 5000000, rate: 9, tenure: 20 },
      { name: 'Home Extension Loan', amount: 2000000, rate: 9, tenure: 10 }
    ],
    'By Profession': [
      { name: 'Home Loan for Self Employed', amount: 4000000, rate: 9, tenure: 20 },
      { name: 'Home Loan for Women', amount: 4000000, rate: 8.25, tenure: 20 }
    ]
  };

  const otherLoanItems = {
    'Other Loans': [
      { name: 'Loan Against Property', amount: 3000000, rate: 9, tenure: 15, icon: <Building className="w-4 h-4" /> },
      { name: 'Loan Against Car', amount: 500000, rate: 10, tenure: 3, icon: <Car className="w-4 h-4" /> },
      { name: 'Micro Loan', amount: 50000, rate: 12, tenure: 2, icon: <Coins className="w-4 h-4" /> },
      { name: 'Two Wheeler Loan', amount: 100000, rate: 11, tenure: 3, icon: <Car className="w-4 h-4" /> },
      { name: 'Gold Loan', amount: 200000, rate: 9.5, tenure: 2, icon: <Coins className="w-4 h-4" /> },
      { name: 'Education Loan', amount: 1000000, rate: 9.5, tenure: 10, icon: <GraduationCap className="w-4 h-4" /> },
      { name: 'Car Loan', amount: 800000, rate: 9, tenure: 5, icon: <Car className="w-4 h-4" /> }
    ]
  };

  const allLoans = {};

  Object.values(businessLoanSubItems).forEach(category => {
    category.forEach(loan => {
      allLoans[loan.name] = loan;
    });
  });

  Object.values(homeLoanSubItems).forEach(category => {
    category.forEach(loan => {
      allLoans[loan.name] = loan;
    });
  });

  Object.values(otherLoanItems).forEach(category => {
    category.forEach(loan => {
      allLoans[loan.name] = loan;
    });
  });

  const loanPresets = {
    home: { amount: 5000000, rate: 8.5, tenure: 20, name: "Home Loan", icon: <Home className="w-5 h-5" /> },
    car: { amount: 800000, rate: 9, tenure: 5, name: "Car Loan", icon: <Car className="w-5 h-5" /> },
    business: { amount: 2000000, rate: 10, tenure: 7, name: "Business Loan", icon: <Briefcase className="w-5 h-5" /> },
    education: { amount: 1000000, rate: 9.5, tenure: 10, name: "Education Loan", icon: <GraduationCap className="w-5 h-5" /> },
    property: { amount: 3000000, rate: 9, tenure: 15, name: "Property Loan", icon: <Building className="w-5 h-5" /> }
  };

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;

      if (hash.startsWith('#calculator-loan=')) {
        const loanName = decodeURIComponent(hash.replace('#calculator-loan=', ''));
        const loan = allLoans[loanName];

        if (loan) {
          setAmount(loan.amount);
          setRate(loan.rate);
          setTenure(loan.tenure);
          setSelectedLoanName(loan.name);

          setTimeout(() => {
            const calculatorElement = document.getElementById('calculator');
            if (calculatorElement) {
              calculatorElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 100);

          setAnimateResult(true);
          setTimeout(() => setAnimateResult(false), 500);
        }
      }
    };

    handleHashChange();
    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  useEffect(() => {
    calculateEmi();
  }, [amount, rate, tenure]);

  const calculateEmi = () => {
    const principal = parseFloat(amount);
    const ratePerMonth = parseFloat(rate) / 12 / 100;
    const numberOfMonths = parseFloat(tenure) * 12;

    if (principal > 0 && ratePerMonth > 0 && numberOfMonths > 0) {
      const emiCalc = (principal * ratePerMonth * Math.pow(1 + ratePerMonth, numberOfMonths)) /
        (Math.pow(1 + ratePerMonth, numberOfMonths) - 1);

      const totalPay = emiCalc * numberOfMonths;
      const totalInt = totalPay - principal;

      setEmi(emiCalc.toFixed(2));
      setTotalPayment(totalPay.toFixed(2));
      setTotalInterest(totalInt.toFixed(2));
      setAnimateResult(true);

      setTimeout(() => setAnimateResult(false), 300);
    }
  };

  const applyPreset = (type) => {
    setLoanType(type);
    setAmount(loanPresets[type].amount);
    setRate(loanPresets[type].rate);
    setTenure(loanPresets[type].tenure);
    setSelectedLoanName(loanPresets[type].name);
  };

  const applyLoanFromMenu = (loan) => {
    setAmount(loan.amount);
    setRate(loan.rate);
    setTenure(loan.tenure);
    setSelectedLoanName(loan.name);
    setShowLoanMenu(false);
  };

  const resetCalculator = () => {
    setAmount(500000);
    setRate(10);
    setTenure(5);
    setLoanType("home");
    setSelectedLoanName("Home Loan");
  };

  const handleApplyNow = () => {
    if (setLoanData) {
      setLoanData({
        amount,
        rate,
        tenure,
        emi,
        totalPayment,
        totalInterest,
        loanType,
        loanTypeName: selectedLoanName
      });
    }
    window.location.hash = 'apply';
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value);
  };

  const principalPercentage = totalPayment ? ((amount / totalPayment) * 100).toFixed(1) : 0;
  const interestPercentage = totalPayment ? ((totalInterest / totalPayment) * 100).toFixed(1) : 0;

  return (
    <div id="calculator" className="w-full overflow-hidden">
      <div className="bg-gradient-to-r from-blue-800 to-blue-900 p-6 md:p-8 text-white">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-yellow-400 rounded-lg flex items-center justify-center">
              <Calculator className="w-7 h-7 text-blue-900" />
            </div>
            <div>
              <h2 className="text-2xl md:text-3xl font-bold">EMI Calculator</h2>
              <p className="text-blue-200 text-sm mt-1">Plan your loan repayment with precision</p>
            </div>
          </div>
          <button
            onClick={resetCalculator}
            className="flex items-center space-x-2 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-lg transition-all duration-200"
          >
            <RefreshCw className="w-4 h-4" />
            <span className="hidden md:inline">Reset</span>
          </button>
        </div>
      </div>

      <div className="p-6 md:p-8">
        <div className="mb-8">
          <label className="block text-sm font-semibold text-gray-700 mb-3">Select Loan Type</label>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="relative">
              <button
                onClick={() => setShowLoanMenu(showLoanMenu === 'business' ? null : 'business')}
                className="w-full p-4 bg-white border-2 border-blue-200 rounded-xl hover:border-blue-500 hover:shadow-lg transition-all duration-200 flex items-center justify-between group"
              >
                <div className="flex items-center space-x-3">
                  <Briefcase className="w-5 h-5 text-blue-800" />
                  <span className="font-semibold text-gray-900">Business Loans</span>
                </div>
                <ChevronDown className={`w-5 h-5 text-gray-600 transition-transform duration-200 ${showLoanMenu === 'business' ? 'rotate-180' : ''}`} />
              </button>

              {showLoanMenu === 'business' && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white border-2 border-blue-200 rounded-xl shadow-xl z-50 max-h-96 overflow-y-auto">
                  {Object.entries(businessLoanSubItems).map(([category, loans]) => (
                    <div key={category} className="border-b last:border-b-0">
                      <div className="bg-blue-50 px-4 py-2 font-semibold text-sm text-blue-900">
                        {category}
                      </div>
                      {loans.map((loan, idx) => (
                        <button
                          key={idx}
                          onClick={() => applyLoanFromMenu(loan)}
                          className="w-full px-4 py-3 text-left hover:bg-blue-50 transition-colors duration-150 flex items-center justify-between group"
                        >
                          <span className="text-gray-700 group-hover:text-blue-800">{loan.name}</span>
                          <span className="text-xs text-gray-500">{loan.rate}% • {loan.tenure}Y</span>
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="relative">
              <button
                onClick={() => setShowLoanMenu(showLoanMenu === 'home' ? null : 'home')}
                className="w-full p-4 bg-white border-2 border-blue-200 rounded-xl hover:border-blue-500 hover:shadow-lg transition-all duration-200 flex items-center justify-between group"
              >
                <div className="flex items-center space-x-3">
                  <Home className="w-5 h-5 text-blue-800" />
                  <span className="font-semibold text-gray-900">Home Loans</span>
                </div>
                <ChevronDown className={`w-5 h-5 text-gray-600 transition-transform duration-200 ${showLoanMenu === 'home' ? 'rotate-180' : ''}`} />
              </button>

              {showLoanMenu === 'home' && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white border-2 border-blue-200 rounded-xl shadow-xl z-50 max-h-96 overflow-y-auto">
                  {Object.entries(homeLoanSubItems).map(([category, loans]) => (
                    <div key={category} className="border-b last:border-b-0">
                      <div className="bg-blue-50 px-4 py-2 font-semibold text-sm text-blue-900">
                        {category}
                      </div>
                      {loans.map((loan, idx) => (
                        <button
                          key={idx}
                          onClick={() => applyLoanFromMenu(loan)}
                          className="w-full px-4 py-3 text-left hover:bg-blue-50 transition-colors duration-150 flex items-center justify-between group"
                        >
                          <span className="text-gray-700 group-hover:text-blue-800">{loan.name}</span>
                          <span className="text-xs text-gray-500">{loan.rate}% • {loan.tenure}Y</span>
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="relative">
              <button
                onClick={() => setShowLoanMenu(showLoanMenu === 'other' ? null : 'other')}
                className="w-full p-4 bg-white border-2 border-blue-200 rounded-xl hover:border-blue-500 hover:shadow-lg transition-all duration-200 flex items-center justify-between group"
              >
                <div className="flex items-center space-x-3">
                  <Coins className="w-5 h-5 text-blue-800" />
                  <span className="font-semibold text-gray-900">Other Loans</span>
                </div>
                <ChevronDown className={`w-5 h-5 text-gray-600 transition-transform duration-200 ${showLoanMenu === 'other' ? 'rotate-180' : ''}`} />
              </button>

              {showLoanMenu === 'other' && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white border-2 border-blue-200 rounded-xl shadow-xl z-50 max-h-96 overflow-y-auto">
                  {Object.entries(otherLoanItems).map(([category, loans]) => (
                    <div key={category}>
                      {loans.map((loan, idx) => (
                        <button
                          key={idx}
                          onClick={() => applyLoanFromMenu(loan)}
                          className="w-full px-4 py-3 text-left hover:bg-blue-50 transition-colors duration-150 flex items-center justify-between group border-b last:border-b-0"
                        >
                          <div className="flex items-center space-x-2">
                            {loan.icon}
                            <span className="text-gray-700 group-hover:text-blue-800">{loan.name}</span>
                          </div>
                          <span className="text-xs text-gray-500">{loan.rate}% • {loan.tenure}Y</span>
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className={`bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between transition-all duration-300 ${animateResult ? 'ring-2 ring-blue-400 shadow-lg' : ''
            }`}>
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-5 h-5 text-blue-800" />
              <span className="text-sm font-semibold text-gray-700">Selected:</span>
              <span className="text-sm text-blue-900 font-bold">{selectedLoanName}</span>
            </div>
          </div>
        </div>

        <div className="mb-8">
          <label className="block text-sm font-semibold text-gray-700 mb-3">Quick Presets</label>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {Object.entries(loanPresets).map(([key, preset]) => (
              <button
                key={key}
                onClick={() => applyPreset(key)}
                className={`p-4 rounded-xl border-2 transition-all duration-300 ${loanType === key
                  ? 'border-blue-800 bg-blue-50 shadow-md'
                  : 'border-gray-200 hover:border-blue-400 hover:bg-blue-50'
                  }`}
              >
                <div className="flex items-center justify-center mb-2">
                  {preset.icon}
                </div>
                <div className="font-semibold text-gray-900 text-sm">{preset.name}</div>
                <div className="text-xs text-gray-600 mt-1">{preset.rate}% • {preset.tenure}Y</div>
              </button>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          <div className="space-y-6">
            <div>
              <label className="flex items-center justify-between text-sm font-semibold text-gray-700 mb-2">
                <span className="flex items-center space-x-2">
                  <IndianRupee className="w-4 h-4 text-blue-800" />
                  <span>Loan Amount</span>
                </span>
                <span className="text-blue-800 font-bold">{formatCurrency(amount)}</span>
              </label>
              <input
                type="range"
                min="10000"
                max="10000000000"
                step="10000"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer accent-blue-800"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>₹10K</span>
                <span>₹1000Cr</span>
              </div>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full mt-2 p-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 outline-none"
                placeholder="Enter loan amount"
              />
            </div>

            <div>
              <label className="flex items-center justify-between text-sm font-semibold text-gray-700 mb-2">
                <span className="flex items-center space-x-2">
                  <TrendingUp className="w-4 h-4 text-blue-800" />
                  <span>Interest Rate (% per annum)</span>
                </span>
                <span className="text-blue-800 font-bold">{rate}%</span>
              </label>
              <input
                type="range"
                min="5"
                max="20"
                step="0.1"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer accent-blue-800"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>5%</span>
                <span>20%</span>
              </div>
              <input
                type="number"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                className="w-full mt-2 p-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 outline-none"
                placeholder="Enter interest rate"
              />
            </div>

            <div>
              <label className="flex items-center justify-between text-sm font-semibold text-gray-700 mb-2">
                <span className="flex items-center space-x-2">
                  <Calendar className="w-4 h-4 text-blue-800" />
                  <span>Loan Tenure (Years)</span>
                </span>
                <span className="text-blue-800 font-bold">{tenure} Years</span>
              </label>
              <input
                type="range"
                min="1"
                max="30"
                step="1"
                value={tenure}
                onChange={(e) => setTenure(e.target.value)}
                className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer accent-blue-800"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>1Y</span>
                <span>30Y</span>
              </div>
              <input
                type="number"
                value={tenure}
                onChange={(e) => setTenure(e.target.value)}
                className="w-full mt-2 p-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 outline-none"
                placeholder="Enter tenure"
              />
            </div>
          </div>

          <div className="space-y-6">
            {emi ? (
              <div className={`transition-all duration-300 ${animateResult ? 'scale-[1.02]' : 'scale-100'}`}>
                <div className="bg-gradient-to-r from-blue-800 to-blue-900 rounded-2xl p-6 text-white mb-6 shadow-xl">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-blue-200 text-sm font-medium">Monthly EMI for {selectedLoanName}</span>
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  </div>
                  <div className="text-3xl sm:text-4xl md:text-5xl font-bold mb-1 break-words">₹{parseFloat(emi).toLocaleString('en-IN')}</div>
                  <div className="text-blue-200 text-sm">Per month for {tenure} years</div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                  <div className="bg-white border-2 border-blue-100 rounded-xl p-4 hover:shadow-lg transition-all duration-200">
                    <div className="text-xs text-gray-600 mb-1">Total Payment</div>
                    <div className="text-lg sm:text-xl font-bold text-gray-900 break-words">₹{parseFloat(totalPayment).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</div>
                  </div>
                  <div className="bg-white border-2 border-orange-100 rounded-xl p-4 hover:shadow-lg transition-all duration-200">
                    <div className="text-xs text-gray-600 mb-1">Total Interest</div>
                    <div className="text-lg sm:text-xl font-bold text-orange-600 break-words">₹{parseFloat(totalInterest).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</div>
                  </div>
                </div>

                <div className="bg-white rounded-xl p-6 border-2 border-gray-100">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-gray-900 flex items-center space-x-2">
                      <PieChart className="w-5 h-5 text-blue-800" />
                      <span>Payment Breakdown</span>
                    </h3>
                  </div>

                  <div className="mb-4">
                    <div className="h-8 bg-gray-100 rounded-lg overflow-hidden flex transition-all duration-300">
                      <div
                        className="bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-center text-white text-xs font-semibold transition-all duration-300"
                        style={{ width: `${principalPercentage}%` }}
                      >
                        {principalPercentage}%
                      </div>
                      <div
                        className="bg-gradient-to-r from-orange-400 to-orange-500 flex items-center justify-center text-white text-xs font-semibold transition-all duration-300"
                        style={{ width: `${interestPercentage}%` }}
                      >
                        {interestPercentage}%
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 bg-gradient-to-r from-blue-500 to-blue-600 rounded"></div>
                        <span className="text-sm text-gray-700">Principal Amount</span>
                      </div>
                      <span className="font-semibold text-gray-900">{formatCurrency(amount)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 bg-gradient-to-r from-orange-400 to-orange-500 rounded"></div>
                        <span className="text-sm text-gray-700">Interest Amount</span>
                      </div>
                      <span className="font-semibold text-orange-600">{formatCurrency(totalInterest)}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 rounded-xl p-4 border-2 border-blue-100 mt-6">
                  <div className="flex items-start space-x-3">
                    <Info className="w-5 h-5 text-blue-800 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-gray-700">
                      <p className="font-semibold text-gray-900 mb-1">Payment Schedule</p>
                      <p>You will pay <span className="font-bold text-blue-800">₹{parseFloat(emi).toLocaleString('en-IN')}</span> monthly for the next <span className="font-bold text-blue-800">{tenure * 12} months</span>.</p>
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleApplyNow}
                  className="w-full bg-gradient-to-r from-blue-800 to-blue-900 text-white py-4 rounded-xl font-semibold hover:from-blue-900 hover:to-blue-950 transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center space-x-2 group mt-6"
                >
                  <span>Apply for {selectedLoanName}</span>

                </button>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center text-gray-500">
                  <Calculator className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-semibold">Adjust the parameters</p>
                  <p className="text-sm">to calculate your EMI</p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-5 border border-blue-200">
            <div className="flex items-center space-x-3 mb-2">
              <div className="w-10 h-10 bg-blue-800 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <h4 className="font-semibold text-gray-900">Competitive Rates</h4>
            </div>
            <p className="text-sm text-gray-600">Get the best interest rates starting from 7.0% per annum</p>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-5 border border-green-200">
            <div className="flex items-center space-x-3 mb-2">
              <div className="w-10 h-10 bg-green-700 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-white" />
              </div>
              <h4 className="font-semibold text-gray-900">Quick Approval</h4>
            </div>
            <p className="text-sm text-gray-600">Fast processing with minimal documentation required</p>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-5 border border-purple-200">
            <div className="flex items-center space-x-3 mb-2">
              <div className="w-10 h-10 bg-purple-700 rounded-lg flex items-center justify-center">
                <Phone className="w-5 h-5 text-white" />
              </div>
              <h4 className="font-semibold text-gray-900">24/7 Support</h4>
            </div>
            <p className="text-sm text-gray-600">Expert assistance available round the clock for your queries</p>
          </div>
        </div>
      </div>
    </div>
  );
}